import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  PieChart, ArrowLeft, Users, DollarSign, TrendingDown
} from 'lucide-react';

export default function ExecutivoSegmentos() {
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);
  const [canonicalCat, setCanonicalCat] = useState(null); // V5.2 · receita por categoria canônica
  const [filtros, setFiltros] = useState({
    mes: new Date().getMonth() + 1,
    ano: new Date().getFullYear()
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    loadDados();
  }, [filtros.mes, filtros.ano]);

  const loadDados = async () => {
    setLoading(true);
    try {
      // V5.2 · shadow mode canônico (?engine=legacy desativa)
      const useLegacy = new URLSearchParams(window.location.search).get('engine') === 'legacy';
      const [response, canonicalRes] = await Promise.all([
        api.get('/executivo/segmentos', { params: filtros }),
        useLegacy
          ? Promise.resolve({ data: null })
          : api.get('/finance/revenue-by-category', { params: { year: filtros.ano } }).catch(() => ({ data: null })),
      ]);
      setDados(response.data);
      if (canonicalRes.data) {
        setCanonicalCat(canonicalRes.data);
        console.info('[segmentos] motor canônico', canonicalRes.data);
      }
    } catch (error) {
      console.error('Erro ao carregar segmentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return `R$ ${(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  };

  const meses = [
    { value: 1, label: 'Janeiro' }, { value: 2, label: 'Fevereiro' }, { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' }, { value: 5, label: 'Maio' }, { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' }, { value: 11, label: 'Novembro' }, { value: 12, label: 'Dezembro' }
  ];

  const cores = {
    'Educação Infantil': 'bg-pink-100 border-pink-300',
    'Fundamental I': 'bg-blue-100 border-blue-300',
    'Fundamental II': 'bg-green-100 border-green-300',
    'Ensino Médio': 'bg-purple-100 border-purple-300',
    'Outros': 'bg-gray-100 border-gray-300'
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-64" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="executivo-segmentos">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/executivo')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PieChart className="w-6 h-6" />
            Análise por Segmento
          </h1>
          <p className="text-muted-foreground">Performance financeira por nível de ensino</p>
        </div>
        <div className="flex gap-2">
          <Select 
            value={String(filtros.mes)} 
            onValueChange={(v) => setFiltros(prev => ({ ...prev, mes: parseInt(v) }))}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {meses.map(m => (
                <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select 
            value={String(filtros.ano)} 
            onValueChange={(v) => setFiltros(prev => ({ ...prev, ano: parseInt(v) }))}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map(ano => (
                <SelectItem key={ano} value={String(ano)}>{ano}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Cards por Segmento */}
      {dados?.segmentos && Object.entries(dados.segmentos).length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(dados.segmentos).map(([nome, seg]) => (
            <Card key={nome} className={`${cores[nome] || cores['Outros']} border-2`}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{nome}</span>
                  <Badge variant="outline" className="text-lg">
                    <Users className="w-4 h-4 mr-1" />
                    {seg.alunos} alunos
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Métricas */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      Receita Faturada
                    </p>
                    <p className="text-lg font-bold">{formatCurrency(seg.receita_faturada)}</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      Receita Recebida
                    </p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(seg.receita_recebida)}</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <TrendingDown className="w-3 h-3" />
                      Inadimplência
                    </p>
                    <p className="text-lg font-bold text-orange-600">{formatCurrency(seg.inadimplencia)}</p>
                    <p className="text-xs text-muted-foreground">{seg.taxa_inadimplencia}%</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-sm text-muted-foreground">Ticket Médio</p>
                    <p className="text-lg font-bold text-purple-600">{formatCurrency(seg.ticket_medio)}</p>
                  </div>
                </div>

                {/* Turmas */}
                {seg.turmas?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2">Turmas ({seg.turmas.length})</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {seg.turmas.map((turma, i) => (
                        <div key={i} className="flex justify-between text-sm p-2 bg-white rounded">
                          <span>{turma.nome}</span>
                          <span className="text-muted-foreground">{turma.alunos} alunos</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <PieChart className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhum dado encontrado para este período.</p>
          </CardContent>
        </Card>
      )}

      {/* V5.2 · Receita canônica por categoria (fonte única db.titles pagos) */}
      {canonicalCat && Object.keys(canonicalCat).length > 0 && (
        <Card className="border-purple-200 bg-purple-50" data-testid="segmentos-canonical-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-purple-700">
              Receita Canônica por Categoria · {filtros.ano}
            </CardTitle>
            <CardDescription className="text-xs">
              Fonte única: db.titles (pagos) · sem drift entre módulos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {Object.entries(canonicalCat).map(([cat, v]) => (
                <li key={cat} className="flex justify-between">
                  <span className="text-purple-700">{cat || 'sem categoria'}</span>
                  <span className="font-mono font-medium">{formatCurrency(v)}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
